import type { AnalyticsInstrumentationEvents } from "@/core/analytics/analytics.events";
import type { AnalyticsEventMap, AnalyticsTraits } from "@/core/analytics/analytics.types";
import { getActiveBus } from "@/core/instrumentation/instrumentation.runtime";

/**
 * The app's telemetry calls as bus publications.
 *
 * Each call publishes its `analytics.*` topic to the page's live bus; the
 * segment sink receives it only while analytics consent has the sink active —
 * a tracked event from before the visitor answers is held and sent if they
 * accept, anything else from before consent is dropped — and the sink, not
 * the caller, owns the vendor's lifecycle. Publishing is therefore
 * consent-blind: callers never check consent, the bus does. No bus (server
 * render, before the mount) means nothing to publish to.
 */
const bus = () => getActiveBus<AnalyticsInstrumentationEvents>();

export const trackEvent = <Event extends keyof AnalyticsEventMap>(
  event: Event,
  properties: AnalyticsEventMap[Event],
): void => {
  bus()?.publish("analytics.track", { event, properties });
};

export const trackPage = (name: string): void => {
  bus()?.publish("analytics.page", { name });
};

export const identify = (userId: string, traits: AnalyticsTraits): void => {
  bus()?.publish("analytics.identify", { userId, traits });
};

/** Ask every active sink to clear its vendor-side identity. */
export const reset = (): void => {
  bus()?.publish("analytics.reset", undefined);
};

export const telemetry = { trackEvent, trackPage, identify, reset };
